import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { makeApi } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TrashLoadRow, TrashDocRow, plural } from "@/components/trash/TrashRows";

const errMsg = (e) => e?.response?.data?.detail || "Something went wrong";

export default function Trash() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [data, setData] = useState(null);
  const [purging, setPurging] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data: d } = await api.get("/trash");
      setData(d);
    } catch (e) {
      setData((p) => p ?? { loads: [], documents: [], days: 30 });
      toast.error(errMsg(e));
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const restore = async (kind, item) => {
    try {
      await api.post(`/trash/${kind}/${item.id}/restore`);
      toast.success(kind === "loads" ? `Load #${item.load_number} restored with its documents` : `${item.original_filename} restored to load #${item.load_number}`);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const purge = async () => {
    setBusy(true);
    try {
      await api.delete(`/trash/${purging.kind}/${purging.item.id}`);
      toast.success("Deleted permanently");
      setPurging(null);
      refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const empty = data && data.loads.length === 0 && data.documents.length === 0;
  const isLoad = purging?.kind === "loads";
  const n = purging?.item.doc_count || 0;
  const purgeText = isLoad
    ? `${n ? `This also deletes its ${plural(n, "document")}` : "It has no documents"}${purging?.item.invoice_number ? ` and invoice ${purging.item.invoice_number}` : ""}.`
    : `${purging?.item.original_filename}.`;

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Trash</h1>
        <p className="text-sm text-slate-500 mt-1">Deleted loads and documents stay here for {data?.days ?? 30} days, then they are removed for good.</p>
      </div>

      {data === null && <div className="text-slate-500">Loading…</div>}
      {empty && (
        <Card className="p-12 border-dashed border-slate-300 text-center" data-testid="trash-empty">
          <Trash2 className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 font-medium">Trash is empty</div>
          <p className="text-sm text-slate-500 mt-1">Anything you delete will show up here first.</p>
        </Card>
      )}

      {data?.loads.length > 0 && (
        <Section title={`Loads (${data.loads.length})`}>
          {data.loads.map((l) => <TrashLoadRow key={l.id} item={l} onRestore={() => restore("loads", l)} onPurge={() => setPurging({ kind: "loads", item: l })} />)}
        </Section>
      )}
      {data?.documents.length > 0 && (
        <Section title={`Documents (${data.documents.length})`}>
          {data.documents.map((d) => <TrashDocRow key={d.id} item={d} onRestore={() => restore("documents", d)} onPurge={() => setPurging({ kind: "documents", item: d })} />)}
        </Section>
      )}

      <ConfirmDialog
        open={!!purging}
        busy={busy}
        title={isLoad ? `Permanently delete load #${purging?.item.load_number}?` : "Permanently delete this document?"}
        confirmLabel="Delete forever"
        testId="purge-dialog"
        onCancel={() => setPurging(null)}
        onConfirm={purge}
        description={<p>{purgeText} This cannot be undone.</p>}
      />
    </div>
  );
}

const Section = ({ title, children }) => (
  <section>
    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{title}</div>
    <Card className="border-slate-200 divide-y divide-slate-100 overflow-hidden">{children}</Card>
  </section>
);
